import { Skeleton, Stack, Typography } from '@customink/pigment-react';
import {
  useItemizedPriceData,
  usePricing,
} from '@customink/project-service-react-components';
import type { ProjectItem } from '@customink/project-service-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import Big from 'big.js';

import { useVolumeDiscountRatio } from '@/hooks/Pricing';
import { useShowStrikethroughPrice } from '@/hooks/useShowStrikethroughPrice';
import { spacing4 } from '@/utils/designTokens';

type ProjectItemPriceProps = {
  item: ProjectItem;
};

export const ProjectItemPrice = ({ item }: ProjectItemPriceProps) => {
  const isNarrowScreen = useMediaQuery('(max-width: 450px)');
  const { isFetching: isFetchingPricing, isLoading: isLoadingPricing } =
    usePricing();

  const showStrikethroughPrice = useShowStrikethroughPrice();

  const itemizedPrice = useItemizedPriceData(item.id || '');

  const inverseVolumeDiscountRatio = useVolumeDiscountRatio(
    item.design?.cid || '',
    true,
  );

  if (!isFetchingPricing && isLoadingPricing) {
    return <Skeleton width={25} />;
  }

  const className = isFetchingPricing ? 'pje-fetching-text' : '';

  if (!itemizedPrice?.totalPrice) {
    return (
      <Typography className={className} whiteSpace="nowrap">
        --
      </Typography>
    );
  }

  const strikethroughPrice = Big(itemizedPrice.totalPrice).times(
    inverseVolumeDiscountRatio,
  );

  return (
    <Stack direction="column" gap={spacing4}>
      {showStrikethroughPrice && (
        <Typography
          fontSize={isNarrowScreen ? '14.4px' : '16px'}
          sx={{ color: 'text.disabled', textDecoration: 'line-through' }}
        >
          {`$` + strikethroughPrice.toFixed(2)}
        </Typography>
      )}
      <Typography
        className={className}
        fontSize={isNarrowScreen ? '14.4px' : '16px'}
      >
        {`$` + Big(itemizedPrice.totalPrice).toFixed(2)}
      </Typography>
    </Stack>
  );
};
